<template>
  <div class="photoelectric-viz">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex>$<strong>公式：</strong>Ek = hν - W₀$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 实验装置渲染 -->
      <div class="canvas-mixed">
        <svg width="100%" height="240" viewBox="0 0 500 240">
           <!-- 真空管 -->
           <rect x="100" y="40" width="300" height="160" rx="80" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="2" />
           
           <!-- 阴极 (阳极) -->
           <rect x="120" y="80" width="10" height="80" fill="#888" />
           <text x="110" y="70" fill="#888" font-size="9">阴极 (K)</text>
           <rect x="370" y="80" width="10" height="80" fill="#444" />
           <text x="360" y="70" fill="#888" font-size="9">阳极 (A)</text>

           <!-- 入射光子 (波动的光束) -->
           <g v-for="i in lightIntensity" :key="'p'+i">
              <path :d="getPhotonPath(i)" fill="none" :stroke="photonColor" stroke-width="2" opacity="0.6">
                 <animate attributeName="stroke-dashoffset" from="100" to="0" dur="1s" repeatCount="indefinite" />
              </path>
           </g>

           <!-- 溢出的光电子 -->
           <g v-for="e in electrons" :key="e.id">
              <circle :cx="e.x" :cy="e.y" r="3" fill="#00d2ff">
                 <animate attributeName="cx" :from="130" :to="370" :dur="`${5/e.speed}s`" repeatCount="indefinite" />
              </circle>
           </g>
        </svg>

        <div class="hud-mini">
           <div class="item"><span>光通量:</span> <span class="val">{{ lightIntensity }} photons/s</span></div>
           <div class="item"><span>电子速度:</span> <span class="val">{{ electronSpeed.toFixed(1) }} c</span></div>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="bottom-controls">
         <div class="control-card glass">
            <div class="row">
               <label>光频率 (λ): {{ wavelength }} nm ({{ frequencyClass }})</label>
               <el-slider v-model="wavelength" :min="200" :max="800" size="mini" reversed></el-slider>
            </div>
            <div class="row">
               <label>光照强度: {{ lightIntensity }}%</label>
               <el-slider v-model="lightIntensity" :min="0" :max="30" size="mini"></el-slider>
            </div>
         </div>

         <div class="lesson-note">
            <p v-if="electronSpeed <= 0" class="warn">🚨 <strong>低于红限频率：</strong> 当前光子能量不足以打出电子，增加光强也无济于事。</p>
            <p v-else>✅ <strong>产生光电效应：</strong> 高频光子具有足够能量克服逸出功。光强仅增加电子数量，频率决定电子动能。</p>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PhotoelectricViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      wavelength: 500,
      lightIntensity: 15,
      electrons: []
    }
  },
  computed: {
    frequencyClass() {
      if (this.wavelength < 300) return "高能紫外线";
      if (this.wavelength < 600) return "可见光";
      return "低能红外线";
    },
    photonColor() {
       if (this.wavelength < 300) return "#a29bfe";
       if (this.wavelength < 450) return "#0984e3";
       if (this.wavelength < 600) return "#00b894";
       return "#d63031";
    },
    electronSpeed() {
       // Simplified: Speed = Max(0, 10 - wavelength/50)
       return Math.max(0, 8 - (this.wavelength / 80));
    }
  },
  watch: {
    lightIntensity() { this.updateElectrons(); },
    wavelength() { this.updateElectrons(); }
  },
  mounted() { this.updateElectrons(); },
  methods: {
    updateElectrons() {
       this.electrons = [];
       if (this.electronSpeed <= 0) return;
       for(let i=0; i<this.lightIntensity/3; i++) {
           this.electrons.push({
               id: i,
               y: 80 + Math.random() * 80,
               speed: this.electronSpeed + Math.random()
           });
       }
    },
    getPhotonPath(i) {
       const startY = 60 + i * 8;
       return `M 20 ${startY} L 120 ${startY + 20}`;
    }
  }
}
</script>

<style scoped lang="scss">
.photoelectric-viz {
  flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden;
}
.viz-header { display: flex; justify-content: space-between; align-items: center; .title { font-size: 1rem; color: #fff; } .formula-chip { color: #ffd700; background: rgba(255,215,0,0.1); padding: 4px 12px; border-radius: 8px; font-weight: bold; } }
.canvas-mixed {
  flex: 1.3; background: #000; border-radius: 12px; position: relative;
  .hud-mini { position: absolute; bottom: 15px; right: 15px; background: rgba(0,0,0,0.6); padding: 8px 12px; border-radius: 8px; .val { color: #00d2ff; } }
}
.bottom-controls { flex: 0.7; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: rgba(255,255,255,0.03); border-radius: 12px; padding: 15px; label { font-size: 0.7rem; color: #888; display: block; margin-bottom: 5px; } }
.lesson-note { 
   background: rgba(255, 215, 0, 0.05); padding: 12px 15px; border-radius: 12px; 
   p { margin: 0; font-size: 0.75rem; color: #aaa; line-height: 1.5; strong { color: #fff; } &.warn { color: #ff4757; background: rgba(255, 71, 87, 0.05); padding: 8px; border-radius: 4px; } }
}
</style>
